import { useEffect, useRef, useState } from "react";
import { Brain } from "lucide-react";
import { useNavigate } from "react-router-dom";
import ConnectionManageSurface from "@/components/connections/ConnectionManageSurface";
import { useLibraryIdentity } from "@/hooks/useLibraryIdentity";
import { useBrainPreferences } from "@/contexts/BrainPreferences";
import { useLanguage } from "@/i18n/LanguageProvider";
import { brainHandoffDestination, openReviewedBrainHandoff, type BrainHandoffPrefill } from "@/lib/brainHandoff";

interface Props {
  open: boolean;
  prefill: BrainHandoffPrefill | null;
  isCurrent: () => boolean;
  onClose: () => void;
  onOpened?: () => void;
}
export function BrainHandoffModal(props: Props) {
  const identity = useLibraryIdentity();
  if (!props.open || !props.prefill || !identity.ready || identity.userId !== props.prefill.expected_user_id) return null;
  return <BrainHandoffContent key={[identity.userId, identity.generation, props.prefill.expected_session_id, props.prefill.action_id].join(":")} {...props} prefill={props.prefill} />;
}
function BrainHandoffContent({ prefill, isCurrent, onClose, onOpened }: Props & { prefill: BrainHandoffPrefill }) {
  const { t } = useLanguage(), navigate = useNavigate(), { setZen } = useBrainPreferences();
  const [busy, setBusy] = useState(false), [error, setError] = useState(false);
  const epoch = useRef(0), lock = useRef(false), current = useRef(isCurrent); current.current = isCurrent;
  useEffect(() => { const lifecycle = epoch; return () => { lifecycle.current++; }; }, []);
  const destination = brainHandoffDestination(prefill.target);
  const open = async () => {
    if (lock.current || !destination) return;
    const version = epoch.current; lock.current = true; setBusy(true); setError(false);
    try {
      await openReviewedBrainHandoff(prefill, { isCurrent: () => version === epoch.current && current.current(), setZen, navigate });
      if (version === epoch.current) { onOpened?.(); onClose(); }
    } catch { if (version === epoch.current) setError(true); }
    finally { lock.current = false; if (version === epoch.current) setBusy(false); }
  };
  return <ConnectionManageSurface open onOpenChange={value => { if (!value && !busy) onClose(); }} title={t("brainAction.handoffTitle")} description={t("brainAction.handoffDescription")} closeLabel={t("common.close")} brand={<Brain className="h-6 w-6" />}>
    <div className="space-y-4">
      <h2>{destination ? t(destination.title) : t("brainAction.handoffError")}</h2>
      {prefill.zen !== undefined && <><p>{t(prefill.zen ? "brainAction.zenOn" : "brainAction.zenOff")}</p><p className="text-sm text-muted-foreground">{t("brainAction.zenDevice")}</p></>}
      {error && <p role="alert">{t("brainAction.handoffError")}</p>}
      <button type="button" className="min-h-11 w-full rounded-xl bg-primary px-4 py-3 font-semibold text-primary-foreground disabled:opacity-50" disabled={busy || !destination} onClick={() => void open()}>{t(busy ? "common.loading" : "brainAction.open")}</button>
    </div>
  </ConnectionManageSurface>;
}
export default BrainHandoffModal;
